// frontend/src/pages/ReviewCycles.js
// Admin page for managing review & ratings cycles: list, add, edit, delete, start/end.
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useActiveDepartment } from '../context/DepartmentContext';
import { getCycles, createCycle, updateCycle, deleteCycle, getDepartments, getCarryForwardSource } from '../services/api';
import {
  Card, Badge, Btn, Modal, Field, Input, Select, Spinner, SectionTitle,
} from '../components/UI';
import PageHeader, { ViewOnlyNotice } from '../components/PageHeader';

const STATUS_COLOR = { upcoming: 'amber', active: 'green', closed: 'gray' };
const STATUS_LABEL = { upcoming: 'Upcoming', active: 'Active', closed: 'Closed' };

const emptyCycleForm = {
  name: '', start_date: '', end_date: '', status: 'upcoming',
  cycle_number: '', year: new Date().getFullYear(), is_annual: false,
  department_id: '', // '' = program-wide (all departments); the program follows the department
  // Optional milestone deadlines between start and end. The reminder cron emails
  // participants 3 days before each; leave blank to skip that reminder.
  kpi_review_date: '', self_assessment_date: '', manager_review_date: '',
  // Carry the previous cycle's KPI plans into this one. Create-only; default on.
  carry_forward_kpis: true,
};

// Milestone metadata for the timeline display (order = chronological).
const MILESTONES = [
  { key: 'kpi_review_date',      label: 'KPI reviews',        hint: 'Managers assign / review KPIs' },
  { key: 'self_assessment_date', label: 'Self-assessment',    hint: 'Employees self-evaluate' },
  { key: 'manager_review_date',  label: 'Manager assessment', hint: 'Managers score & finalise' },
];

export default function ReviewCycles() {
  const { user } = useAuth();
  const [cycles, setCycles]     = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(emptyCycleForm);
  const [filter, setFilter]     = useState('all');        // all | upcoming | active | closed
  // What carrying forward WOULD copy, for the create form's hint line, and what
  // it actually DID copy, reported after a successful create.
  const [carrySrc, setCarrySrc]       = useState(null);   // null = not looked up yet
  const [carryLoading, setCarryLoading] = useState(false);
  const [carryReport, setCarryReport] = useState(null);

  const { activeDepartmentId } = useActiveDepartment();
  const isAdmin    = user?.role === 'admin';
  const isDeptHead = user?.role === 'dept_head';
  // Dept heads manage cycles only while viewing their own department; else read-only.
  const canManageHere = isAdmin || (isDeptHead && activeDepartmentId != null && Number(activeDepartmentId) === Number(user?.department_id));

  const load = useCallback(async () => {
    try {
      const [c, d] = await Promise.all([
        getCycles(),
        getDepartments().catch(() => []),
      ]);
      setCycles(Array.isArray(c) ? c : []);
      setDepartments(Array.isArray(d) ? d : []);
    } catch { setCycles([]); }
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  // Resolve the source cycle for the hint line. Create only — carry-forward is a
  // creation-time act, so editing an existing cycle never shows this.
  useEffect(() => {
    if (!showForm || editing || !form.start_date) { setCarrySrc(null); return; }
    let alive = true;
    setCarryLoading(true);
    getCarryForwardSource({
      start_date: form.start_date,
      ...(form.department_id ? { department_id: Number(form.department_id) } : {}),
    })
      .then(d => { if (alive) setCarrySrc(d); })
      .catch(() => { if (alive) setCarrySrc(null); })
      .finally(() => { if (alive) setCarryLoading(false); });
    return () => { alive = false; };
  }, [showForm, editing, form.start_date, form.department_id]);

  const openCreate = () => {
    setEditing(null);
    // Default to the department currently in view (program follows the department).
    setForm({ ...emptyCycleForm, department_id: activeDepartmentId ? String(activeDepartmentId) : '' });
    setShowForm(true);
  };

  const openEdit = (cycle) => {
    setEditing(cycle);
    setForm({
      name: cycle.name || '',
      start_date: cycle.start_date || '',
      end_date: cycle.end_date || '',
      status: cycle.status || 'upcoming',
      cycle_number: cycle.cycle_number || '',
      year: cycle.year || new Date().getFullYear(),
      is_annual: !!cycle.is_annual,
      department_id: cycle.department_id ? String(cycle.department_id) : '',
      kpi_review_date: cycle.kpi_review_date || '',
      self_assessment_date: cycle.self_assessment_date || '',
      manager_review_date: cycle.manager_review_date || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        is_annual: form.is_annual ? 1 : 0,
        cycle_number: form.cycle_number || null,
        // No program_id — the backend derives the program from the department.
        department_id: form.department_id ? Number(form.department_id) : null,
        // Send blank milestone dates as null so the backend clears the column.
        kpi_review_date: form.kpi_review_date || null,
        self_assessment_date: form.self_assessment_date || null,
        manager_review_date: form.manager_review_date || null,
      };
      if (editing) {
        await updateCycle(editing.id, payload);
      } else {
        const res = await createCycle(payload);
        setCarryReport(res?.carry_forward_error
          ? { error: res.carry_forward_error }
          : (res?.carry_forward || null));
      }
      await load();
      setShowForm(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving cycle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cycle) => {
    if (!window.confirm(`Archive "${cycle.name}"? It will be hidden from the system.`)) return;
    try {
      await deleteCycle(cycle.id);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error archiving cycle');
    }
  };

  const handleStatusChange = async (cycle, newStatus) => {
    const action = newStatus === 'active' ? 'start' : 'close';
    if (!window.confirm(`${action === 'start' ? 'Start' : 'Close'} cycle "${cycle.name}"?`)) return;
    try {
      await updateCycle(cycle.id, { ...cycle, status: newStatus, is_annual: cycle.is_annual ? 1 : 0 });
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating cycle status');
    }
  };

  const filtered = cycles.filter(c => filter === 'all' || c.status === filter);
  const activeCycles = cycles.filter(c => c.status === 'active').length;
  const totalReviews = cycles.reduce((sum, c) => sum + (parseInt(c.total_reviews) || 0), 0);

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="review-cycles" helpTask="open-review-cycle" subtitle={`${cycles.length} cycle${cycles.length !== 1 ? 's' : ''} total · ${activeCycles} active · ${totalReviews} reviews submitted`} />
        {canManageHere && (
          <Btn size="sm" onClick={openCreate}>+ New cycle</Btn>
        )}
      </div>

      {!canManageHere && <ViewOnlyNotice />}

      {/* Closing means different things per program, and nothing in the UI said so. */}
      {canManageHere && (
        <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '10px 14px',
                      marginBottom: 14, fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
          <strong>Closing a cycle</strong> freezes its reviews and KPI ratings as historical
          record for Tech &amp; Product — set it back to Active to edit them again. Sales
          cycles are not locked by closing. Marking cycle 3 as annual is what triggers
          end-of-year bonus generation.
        </div>
      )}

      {/* What carry-forward ACTUALLY did, reported after a create — not what was
          requested. Dismissible, since it's a one-off confirmation. */}
      {carryReport && (
        <div style={{
          background: carryReport.error ? 'var(--red-50)' : 'var(--navy-50)',
          color: carryReport.error ? 'var(--red-700)' : 'var(--navy-600)',
          borderRadius: 8, padding: '11px 14px', marginBottom: 14, fontSize: 12.5,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ flex: 1 }}>
            {carryReport.error || <>
              Carried <strong>{carryReport.templates}</strong> KPI{carryReport.templates === 1 ? '' : 's'} forward
              from <strong>{carryReport.source_cycle_name}</strong> — {carryReport.assignments} assignment
              {carryReport.assignments === 1 ? '' : 's'} across {carryReport.people} {carryReport.people === 1 ? 'person' : 'people'},
              weights preserved
              {carryReport.excluded > 0 && `, ${carryReport.excluded} previously removed KPI${carryReport.excluded === 1 ? '' : 's'} left removed`}.
              {' '}Edit them under KPI setup.
            </>}
          </span>
          <button onClick={() => setCarryReport(null)} aria-label="Dismiss"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 15, lineHeight: 1, padding: 2 }}>×</button>
        </div>
      )}

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total cycles', value: cycles.length, color: 'var(--blue-700)' },
          { label: 'Active', value: activeCycles, color: 'var(--navy-600)' },
          { label: 'Upcoming', value: cycles.filter(c => c.status === 'upcoming').length, color: 'var(--amber-700)' },
          { label: 'Closed', value: cycles.filter(c => c.status === 'closed').length, color: 'var(--text-3)' },
        ].map(stat => (
          <Card key={stat.label} style={{ padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 500, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{stat.label}</div>
          </Card>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['all', 'active', 'upcoming', 'closed'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 500, border: 'none',
            borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'inherit',
            background: filter === f ? 'var(--navy-50)' : 'transparent',
            color: filter === f ? 'var(--navy-600)' : 'var(--text-3)',
            transition: 'all 0.12s',
          }}>
            {f === 'all' ? 'All' : STATUS_LABEL[f]} {f !== 'all' && `(${cycles.filter(c => c.status === f).length})`}
          </button>
        ))}
      </div>

      {/* Cycles table */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)' }}>
              {['Cycle name', 'Department', 'Period', 'Status', 'Cycle #', 'Year', 'Reviews', 'Employees', 'Created by', ...(canManageHere ? ['Actions'] : [])].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase',
                  letterSpacing: '0.5px', color: 'var(--text-3)',
                  borderBottom: '0.5px solid var(--border)', fontWeight: 500,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                {/* Name + annual badge */}
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ fontWeight: 500 }}>{c.name}</div>
                  {!!c.is_annual && (
                    <span style={{ fontSize: 10, color: 'var(--amber-700)', fontWeight: 500 }}>Annual (bonus)</span>
                  )}
                </td>
                {/* Department (the program is implied by the department) */}
                <td style={{ padding: '12px 14px' }}>
                  {c.department_name
                    ? <Badge color="green">{c.department_name}</Badge>
                    : <span style={{ color: 'var(--text-3)', fontSize: 12 }}>All departments</span>}
                </td>
                {/* Period */}
                <td style={{ padding: '12px 14px', color: 'var(--text-2)', fontSize: 12 }}>
                  {c.start_date} &rarr; {c.end_date}
                </td>
                {/* Status */}
                <td style={{ padding: '12px 14px' }}>
                  <Badge color={STATUS_COLOR[c.status]}>{STATUS_LABEL[c.status] || c.status}</Badge>
                </td>
                {/* Cycle # */}
                <td style={{ padding: '12px 14px', color: 'var(--text-2)' }}>
                  {c.cycle_number ? `#${c.cycle_number}` : '--'}
                </td>
                {/* Year */}
                <td style={{ padding: '12px 14px', color: 'var(--text-2)' }}>
                  {c.year || '--'}
                </td>
                {/* Reviews count */}
                <td style={{ padding: '12px 14px' }}>
                  <span style={{ fontWeight: 500 }}>{c.submitted_reviews || 0}</span>
                  <span style={{ color: 'var(--text-3)', fontSize: 11 }}>/{c.total_reviews || 0}</span>
                </td>
                {/* Employees reviewed */}
                <td style={{ padding: '12px 14px', color: 'var(--text-2)' }}>
                  {c.employees_reviewed || 0}
                </td>
                {/* Created by */}
                <td style={{ padding: '12px 14px', color: 'var(--text-3)', fontSize: 12 }}>
                  {c.created_by_name || '--'}
                </td>
                {/* Actions */}
                {canManageHere && (
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {c.status === 'upcoming' && (
                        <Btn variant="secondary" size="sm" onClick={() => handleStatusChange(c, 'active')}
                          style={{ color: 'var(--navy-600)' }}>Start</Btn>
                      )}
                      {c.status === 'active' && (
                        <Btn variant="secondary" size="sm" onClick={() => handleStatusChange(c, 'closed')}
                          style={{ color: 'var(--amber-700)' }}>End</Btn>
                      )}
                      {c.status === 'closed' && (
                        <Btn variant="secondary" size="sm" onClick={() => handleStatusChange(c, 'active')}
                          style={{ color: 'var(--navy-600)', fontSize: 11 }}>Reopen</Btn>
                      )}
                      <Btn variant="secondary" size="sm" onClick={() => openEdit(c)}>Edit</Btn>
                      <Btn variant="ghost" size="sm" onClick={() => handleDelete(c)}
                        style={{ color: 'var(--red-500)' }}>Archive</Btn>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={canManageHere ? 10 : 9} style={{
                  padding: '32px 14px', textAlign: 'center', color: 'var(--text-3)',
                }}>
                  {filter !== 'all' ? `No ${filter} cycles.` : 'No review cycles yet. Create one to get started.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Cycle detail cards below table */}
      {filtered.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <SectionTitle>Cycle details</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14, marginTop: 10 }}>
            {filtered.map(c => {
              const totalDays = Math.max(1, Math.ceil((new Date(c.end_date) - new Date(c.start_date)) / 86400000));
              const elapsed = Math.max(0, Math.ceil((new Date() - new Date(c.start_date)) / 86400000));
              const progressPct = c.status === 'closed' ? 100 : Math.min(100, Math.round((elapsed / totalDays) * 100));
              const daysLeft = Math.max(0, Math.ceil((new Date(c.end_date) - new Date()) / 86400000));

              return (
                <Card key={c.id} style={{ padding: '16px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                        {c.start_date} &rarr; {c.end_date}
                      </div>
                    </div>
                    <Badge color={STATUS_COLOR[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                  </div>

                  {/* Progress bar */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>
                      <span>{progressPct}% elapsed</span>
                      <span>{c.status === 'closed' ? 'Completed' : `${daysLeft} days left`}</span>
                    </div>
                    <div style={{ height: 5, background: 'var(--gray-100)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 3, transition: 'width 0.3s',
                        width: `${progressPct}%`,
                        background: c.status === 'active' ? 'var(--navy-500)' :
                          c.status === 'closed' ? 'var(--gray-400)' : 'var(--amber-400)',
                      }} />
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {[
                      { label: 'Reviews', value: c.total_reviews || 0 },
                      { label: 'Submitted', value: c.submitted_reviews || 0 },
                      { label: 'Employees', value: c.employees_reviewed || 0 },
                    ].map(s => (
                      <div key={s.label} style={{ textAlign: 'center', padding: '6px 0', background: 'var(--gray-50)', borderRadius: 6 }}>
                        <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-1)' }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Milestone timeline */}
                  {MILESTONES.some(m => c[m.key]) && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', marginBottom: 6 }}>
                        Milestones
                      </div>
                      {MILESTONES.filter(m => c[m.key]).map(m => (
                        <div key={m.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                          <span style={{ color: 'var(--text-2)' }}>
                            <span style={{ display: 'inline-block', width: 6, height: 6, background: 'var(--navy-500)', borderRadius: 2, marginRight: 7, verticalAlign: 'middle' }} />
                            {m.label}
                          </span>
                          <span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{c[m.key]}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Annual badge */}
                  {!!c.is_annual && (
                    <div style={{
                      marginTop: 10, padding: '6px 10px', background: 'var(--amber-50)',
                      border: '0.5px solid #FAC775', borderRadius: 6,
                      fontSize: 11, color: 'var(--amber-700)',
                    }}>
                      Annual cycle -- KPI scores feed into year-end bonus calculations
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Create / Edit cycle modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)}
        title={editing ? `Edit cycle -- ${editing.name}` : 'New review cycle'} width={480}>
        <form onSubmit={handleSubmit}>
          <Field label="Cycle name" required>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Cycle 1 - Jan-Apr 2027" />
          </Field>
          <Field label="Department">
            <Select value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
              <option value="">Program-wide (all departments)</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.program_name ? ` · ${d.program_name}` : ''}
                </option>
              ))}
            </Select>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
              The program is set automatically from the department you choose.
            </div>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Start date" required>
              <Input type="date" value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </Field>
            <Field label="End date" required>
              <Input type="date" value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Cycle #">
              <Select value={form.cycle_number}
                onChange={e => setForm(f => ({ ...f, cycle_number: e.target.value }))}>
                <option value="">--</option>
                <option value="1">1 (Jan-Apr)</option>
                <option value="2">2 (Apr-Aug)</option>
                <option value="3">3 (Aug-Dec)</option>
              </Select>
            </Field>
            <Field label="Year">
              <Input type="number" value={form.year}
                onChange={e => setForm(f => ({ ...f, year: e.target.value }))} placeholder="2026" />
            </Field>
            <Field label="Status">
              <Select value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </Select>
            </Field>
          </div>
          <div style={{ margin: '4px 0 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', marginBottom: 2 }}>
              Milestone deadlines
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 10 }}>
              Optional. The system emails a reminder 3 days before each date to whoever
              still has that step outstanding, and announces the start &amp; end of the cycle.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              {MILESTONES.map(mstone => (
                <Field key={mstone.key} label={mstone.label}>
                  <Input type="date" value={form[mstone.key]}
                    onChange={e => setForm(f => ({ ...f, [mstone.key]: e.target.value }))} />
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 3 }}>{mstone.hint}</div>
                </Field>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <input type="checkbox" id="is_annual_cb" checked={form.is_annual}
              onChange={e => setForm(f => ({ ...f, is_annual: e.target.checked }))} />
            <label htmlFor="is_annual_cb" style={{ fontSize: 13, cursor: 'pointer' }}>
              Annual cycle (Dec) -- triggers bonus calculation
            </label>
          </div>
          {/* Carry-forward is a creation-time act, so this never appears on edit.
              Without it a new cycle starts with an empty KPI catalogue and every
              per-person weight has to be re-tuned by hand. */}
          {!editing && (
            <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '11px 13px', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" id="carry_kpis_cb" checked={form.carry_forward_kpis}
                  onChange={e => setForm(f => ({ ...f, carry_forward_kpis: e.target.checked }))} />
                <label htmlFor="carry_kpis_cb" style={{ fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                  Carry forward KPIs from the previous cycle
                </label>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6, paddingLeft: 22, lineHeight: 1.5 }}>
                {!form.start_date
                  ? 'Set a start date to see which cycle this would copy from.'
                  : carryLoading
                    ? 'Checking for a previous cycle…'
                    : carrySrc?.source_cycle
                      ? <>
                          From <strong>{carrySrc.source_cycle.name}</strong> — {carrySrc.templates} KPI
                          {carrySrc.templates === 1 ? '' : 's'} across {carrySrc.people} {carrySrc.people === 1 ? 'person' : 'people'},
                          weights preserved
                          {carrySrc.excluded > 0 && `, ${carrySrc.excluded} previously removed KPI${carrySrc.excluded === 1 ? '' : 's'} stay removed`}.
                        </>
                      : 'No earlier cycle to copy from — this cycle will start with an empty KPI catalogue.'}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : editing ? 'Save changes' : 'Create cycle'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
